import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixRestaurantsMenusV1Menu from './restaurants-menus-v1-menu-menus.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface Menu {
  /**
   * Menu ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the menu was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the menu was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Menu name.
   * @minLength 1
   * @maxLength 500
   */
  name?: string;
  /**
   * Menu description.
   * @maxLength 1500
   */
  description?: string | null;
  /** Whether the menu visible to site visitors. */
  visible?: boolean | null;
  /**
   * Menu section IDs.
   * @format GUID
   * @maxSize 100
   */
  sectionIds?: string[];
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.
   * @maxLength 500
   */
  urlQueryParam?: string | null;
  /** Custom SEO data for the menu. */
  seoData?: SeoSchema;
  /**
   * ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.
   * @format GUID
   * @immutable
   * @readonly
   */
  businessLocationId?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface BusinessLocationDetails {
  /**
   * Location name.
   * @maxLength 150
   * @readonly
   */
  name?: string | null;
  /**
   * Whether the location is archived.
   *
   * Archived locations are hidden and ignored on restaurants site pages, and marked as archived in the dashboard.
   * @readonly
   */
  archived?: boolean | null;
  /**
   * Whether this location is the default location for the business.
   * @readonly
   */
  default?: boolean | null;
}

export interface ExternalReferenceInfo {
  /**
   * ID of the external integrator app\system associated with this object.
   * @format GUID
   */
  externalAppId?: string | null;
  /**
   * Reference to an external resource ID. Used to link this object to a specific entity in an external system.
   * @maxLength 100
   */
  externalEntityId?: string | null;
  /** Last time this menu was synced with the external app. */
  lastSyncDate?: Date | null;
  /** Whether the connection to the external system is currently active. */
  connected?: boolean | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface DeleteOrphanSections {
  /** Menu id */
  menuId?: string;
}

export interface MenusDataCloningCompleted {}

export interface CreateMenuRequest {
  /** Menu details. */
  menu: Menu;
}

export interface CreateMenuResponse {
  /** Menu. */
  menu?: Menu;
}

export interface BulkCreateMenusRequest {
  /**
   * Menu details.
   * @maxSize 100
   */
  menus: Menu[];
  /** Whether to receive the created menus in the response. */
  returnEntity?: boolean;
}

export interface BulkCreateMenusResponse {
  /** Information about the created menus. */
  results?: BulkCreateMenuResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateMenuResult {
  /** Whether to receive the created menus in the response. */
  menuMetadata?: ItemMetadata;
  /** Created menu. */
  menu?: Menu;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetMenuRequest {
  /**
   * Menu ID.
   * @format GUID
   */
  menuId: string;
}

export interface GetMenuResponse {
  /** Menu. */
  menu?: Menu;
}

export interface ListMenusRequest {
  /**
   * Menu IDs.
   * @format GUID
   * @maxSize 500
   */
  menuIds?: string[];
  /** The metadata of the paginated results. */
  paging?: CursorPaging;
  /** Whether to return only menus that are visible to site visitors. */
  onlyVisible?: boolean | null;
}

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 500
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface ListMenusResponse {
  /** Retrieved menus. */
  menus?: Menu[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface QueryMenusRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryMenusResponse {
  /** Retrieved menus. */
  menus?: Menu[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface UpdateMenuRequest {
  /** Menu to update. */
  menu: Menu;
}

export interface UpdateMenuResponse {
  /** Updated menu. */
  menu?: Menu;
}

export interface BulkUpdateMenuRequest {
  /**
   * Menus to update.
   * @minSize 1
   * @maxSize 100
   */
  menus: MaskedMenu[];
  /** Whether to receive the entity in the response. */
  returnEntity?: boolean;
}

export interface MaskedMenu {
  /** Menu to updated. */
  menu?: Menu;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateMenuResponse {
  /**
   * Results of bulk menu update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkMenuResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkMenuResult {
  /** Metadata for menu update. */
  menuMetadata?: ItemMetadata;
  /** Updated menu. Only returned if `returnEntity` is set to `true`. */
  menu?: Menu;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Namespace of the app. */
  namespace?: string;
  /** Updated extended fields data. */
  namespaceData?: Record<string, any> | null;
}

export interface DeleteMenuRequest {
  /**
   * Menu ID.
   * @format GUID
   */
  menuId: string;
}

export interface DeleteMenuResponse {}

export interface RunMenusMultiLocationMigrationRequest {
  /**
   * business location id
   * @format GUID
   */
  businessLocationId?: string;
}

export interface RunMenusMultiLocationMigrationResponse {
  /** Whether menus multi-location migration finished successfully */
  success?: boolean;
}

export interface UpdateBusinessLocationIdRequest {
  /** @format GUID */
  menuId?: string;
  /** @format GUID */
  businessLocationId?: string | null;
}

export interface UpdateBusinessLocationIdResponse {}

export interface CloneMenusRequest {
  /**
   * The MetaSiteId to clone from.
   * @format GUID
   */
  metaSiteId?: string;
}

export interface CloneMenusResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface SyncContentToMultilingualRequest {
  /**
   * MetaSite ID
   * @format GUID
   */
  metaSiteId?: string;
}

export interface SyncContentToMultilingualResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface DuplicateMenuRequest {
  /**
   * Menu id to be duplicated.
   * @format GUID
   */
  _id: string;
  /**
   * Business location ids to duplicate the menu to.
   * @format GUID
   * @maxSize 100
   */
  businessLocationIds?: string[] | null;
  /** Indicates whether sub-entities (such as items) should be duplicated as well. */
  duplicateSubEntities?: boolean | null;
  /**
   * The new name of the duplicated menu.
   * @maxLength 500
   */
  menuName?: string | null;
}

export interface DuplicateMenuResponse {
  /** @format GUID */
  menuIds?: string[];
}

export interface GetMenuSiteUrlRequest {
  /**
   * Menu ID.
   * @format GUID
   * @readonly
   */
  _id: string | null;
}

export interface GetMenuSiteUrlResponse {
  /** Retrieved menuInfo with path url. */
  menuSiteUrl?: MenuSiteUrl;
}

export interface MenuSiteUrl {
  /**
   * Path URL.
   * @minLength 1
   * @maxLength 1000
   */
  path?: string;
}

export interface QueryMenusSiteUrlRequest {
  /** Query options */
  query?: CursorQuery;
}

export interface QueryMenusSiteUrlResponse {
  /** Retrieved menuInfos with path urls. */
  menuSiteUrls?: MenuSiteUrl[];
  /** The metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface MenuCreatedEnvelope {
  entity: Menu;
  metadata: EventMetadata;
}

/** @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MENU_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.menu_created
 * @serviceIdentifier com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu
 * @slug created
 * @documentationMaturity preview
 */
export declare function onMenuCreated(
  handler: (event: MenuCreatedEnvelope) => void | Promise<void>
): void;

export interface MenuDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a menu is deleted.
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MENU_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.menu_deleted
 * @serviceIdentifier com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onMenuDeleted(
  handler: (event: MenuDeletedEnvelope) => void | Promise<void>
): void;

export interface MenuUpdatedEnvelope {
  entity: Menu;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId RESTAURANTS.MENU_READ
 * @webhook
 * @eventType wix.restaurants.menus.v1.menu_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onMenuUpdated(
  handler: (event: MenuUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * > **Note:** The Menus API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates a menu.
 *
 * To create multiple menus at once, use [Bulk Create Menus](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/menu/bulk-create-menus).
 * @param menu - Menu details.
 * @public
 * @documentationMaturity preview
 * @requiredField menu
 * @permissionId RESTAURANTS.MENU_CREATE
 * @applicableIdentity APP
 * @returns Menu.
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.CreateMenu
 */
export async function createMenu(
  menu: Menu
): Promise<
  NonNullablePaths<
    Menu,
    | `name`
    | `sectionIds`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ menu: menu });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.createMenu(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.menu!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { menu: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['menu']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Creates multiple menus at once.
 * @param menus - Menu details.
 * @public
 * @documentationMaturity preview
 * @requiredField menus
 * @permissionId RESTAURANTS.MENU_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.BulkCreateMenus
 */
export async function bulkCreateMenus(
  menus: Menu[],
  options?: BulkCreateMenusOptions
): Promise<
  NonNullablePaths<
    BulkCreateMenusResponse,
    | `results`
    | `results.${number}.menuMetadata.originalIndex`
    | `results.${number}.menuMetadata.success`
    | `results.${number}.menuMetadata.error.code`
    | `results.${number}.menuMetadata.error.description`
    | `results.${number}.menu.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menus: menus,
    returnEntity: options?.returnEntity,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.bulkCreateMenus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          menus: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['menus', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateMenusOptions {
  /** Whether to receive the created menus in the response. */
  returnEntity?: boolean;
}

/**
 * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a menu by ID.
 * @param menuId - Menu ID.
 * @public
 * @documentationMaturity preview
 * @requiredField menuId
 * @permissionId RESTAURANTS.MENU_READ
 * @applicableIdentity APP
 * @returns Menu.
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.GetMenu
 */
export async function getMenu(
  menuId: string
): Promise<
  NonNullablePaths<
    Menu,
    | `name`
    | `sectionIds`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ menuId: menuId });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.getMenu(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.menu!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { menuId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['menuId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Retrieves a list of up to 500 menus.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.MENU_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.ListMenus
 */
export async function listMenus(
  options?: ListMenusOptions
): Promise<
  NonNullablePaths<
    ListMenusResponse,
    | `menus`
    | `menus.${number}.name`
    | `menus.${number}.seoData.settings.preventAutoRedirect`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menuIds: options?.menuIds,
    paging: options?.paging,
    onlyVisible: options?.onlyVisible,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.listMenus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          menuIds: '$[0].menuIds',
          paging: '$[0].paging',
          onlyVisible: '$[0].onlyVisible',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListMenusOptions {
  /**
   * Menu IDs.
   * @format GUID
   * @maxSize 500
   */
  menuIds?: string[];
  /** The metadata of the paginated results. */
  paging?: CursorPaging;
  /** Whether to return only menus that are visible to site visitors. */
  onlyVisible?: boolean | null;
}

/**
 * Creates a query to retrieve a list of menus.
 *
 * The `queryMenus()` function builds a query to retrieve a list of menus and returns a `MenusQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](/menus/menus-query-builder/find) function.
 *
 * You can refine the query by chaining `MenusQueryBuilder` functions onto the query. `MenusQueryBuilder` functions enable you to filter, sort, and control the results that `queryMenus()` returns.
 *
 * `queryMenus()` runs with the following `MenusQueryBuilder` defaults, which you can override:
 *
 * * [`limit(100)`](/menus/menus-query-builder/limit)
 *
 * The following `MenusQueryBuilder` functions are supported for `queryMenus()`. For a full description of the menu object, see the object returned for the [`items`](/menus/menus-query-result/items) property in `MenusQueryResult`.
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.MENU_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.QueryMenus
 */
export function queryMenus(): MenusQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Menu, 'CURSOR', QueryMenusRequest, QueryMenusResponse>({
    func: async (payload: QueryMenusRequest) => {
      const reqOpts = ambassadorWixRestaurantsMenusV1Menu.queryMenus(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryMenusRequest['query']) => {
      const args = [query, {}] as [QueryMenusRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryMenusResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.menus,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface MenusQueryResult extends QueryCursorResult {
  items: Menu[];
  query: MenusQueryBuilder;
  next: () => Promise<MenusQueryResult>;
  prev: () => Promise<MenusQueryResult>;
}

export interface MenusQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'visible'
      | 'urlQueryParam',
    value: any
  ) => MenusQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'description'
      | 'visible'
      | 'urlQueryParam',
    value: any
  ) => MenusQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => MenusQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => MenusQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => MenusQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_createdDate' | '_updatedDate',
    value: any
  ) => MenusQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: 'name' | 'description' | 'urlQueryParam',
    value: string
  ) => MenusQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (propertyName: string, value: any[]) => MenusQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName: '_id' | 'name' | 'description' | 'urlQueryParam',
    value: any
  ) => MenusQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      '_createdDate' | '_updatedDate' | 'name' | 'description' | 'urlQueryParam'
    >
  ) => MenusQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      '_createdDate' | '_updatedDate' | 'name' | 'description' | 'urlQueryParam'
    >
  ) => MenusQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => MenusQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => MenusQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<MenusQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.QueryMenus
 * @requiredField query
 */
export async function typedQueryMenus(
  query: MenuQuery
): Promise<
  NonNullablePaths<
    QueryMenusResponse,
    | `menus`
    | `menus.${number}.name`
    | `menus.${number}.seoData.settings.preventAutoRedirect`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.queryMenus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface MenuQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id'];
      operators: ['$eq', '$in', '$ne', '$nin'];
      sort: 'NONE';
    },
    {
      fields: ['sectionIds'];
      operators: ['$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['description', 'name', 'urlQueryParam'];
      operators: ['$eq', '$in', '$ne', '$nin', '$startsWith'];
      sort: 'BOTH';
    },
    {
      fields: ['_createdDate', '_updatedDate'];
      operators: ['$eq', '$gt', '$gte', '$lt', '$lte', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['visible'];
      operators: ['$eq', '$ne'];
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Menu, MenuQuerySpec>;
export type MenuQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 500 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Menu, MenuQuerySpec, MenuQuery>(),
  },
};

/**
 * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates a menu.
 *
 * To update multiple menus at once, use [Bulk Update Menu](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/menu/bulk-update-menu).
 *
 * Each time a menu is updated, its revision increments by 1. The existing revision must be included when updating a menu. This ensures you're working with the latest menu information, and it prevents unintended overwrites.
 * @param _id - Menu ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField menu
 * @requiredField menu.revision
 * @permissionId RESTAURANTS.MENU_UPDATE
 * @applicableIdentity APP
 * @returns Updated menu.
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.UpdateMenu
 */
export async function updateMenu(
  _id: string,
  menu: NonNullablePaths<UpdateMenu, `revision`, 2>
): Promise<
  NonNullablePaths<
    Menu,
    | `name`
    | `sectionIds`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menu: { ...menu, id: _id },
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.updateMenu(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.menu!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { menu: '$[1]' },
        explicitPathsToArguments: { 'menu.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'menu']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateMenu {
  /**
   * Menu ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the menu is updated. To prevent conflicting changes, the current revision must be passed when updating the menu. Ignored when creating a menu.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the menu was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the menu was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Menu name.
   * @minLength 1
   * @maxLength 500
   */
  name?: string;
  /**
   * Menu description.
   * @maxLength 1500
   */
  description?: string | null;
  /** Whether the menu visible to site visitors. */
  visible?: boolean | null;
  /**
   * Menu section IDs.
   * @format GUID
   * @maxSize 100
   */
  sectionIds?: string[];
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Part of the site URL, that redirects to the menu. For example, in the URL `www.mywebsite.com/our-menus/dinner-menu`, `dinner-menu` is the field value.
   * @maxLength 500
   */
  urlQueryParam?: string | null;
  /** Custom SEO data for the menu. */
  seoData?: SeoSchema;
  /**
   * ID of the business location ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where this menu is available.
   * @format GUID
   * @immutable
   * @readonly
   */
  businessLocationId?: string | null;
}

/**
 * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates multiple menus at once.
 *
 * Each time a menu is updated, its revision increments by 1. The existing revision must be included when updating the menu. This ensures you're working with the latest menu information, and it prevents unintended overwrites.
 * @param menus - Menus to update.
 * @public
 * @documentationMaturity preview
 * @requiredField menus
 * @requiredField menus.menu._id
 * @requiredField menus.menu.revision
 * @permissionId RESTAURANTS.MENU_UPDATE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.BulkUpdateMenu
 */
export async function bulkUpdateMenu(
  menus: NonNullablePaths<MaskedMenu, `menu._id` | `menu.revision`, 3>[],
  options?: BulkUpdateMenuOptions
): Promise<
  NonNullablePaths<
    BulkUpdateMenuResponse,
    | `results`
    | `results.${number}.menuMetadata.originalIndex`
    | `results.${number}.menuMetadata.success`
    | `results.${number}.menuMetadata.error.code`
    | `results.${number}.menuMetadata.error.description`
    | `results.${number}.menu.name`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    menus: menus,
    returnEntity: options?.returnEntity,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.bulkUpdateMenu(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          menus: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['menus', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateMenuOptions {
  /** Whether to receive the entity in the response. */
  returnEntity?: boolean;
}

/**
 * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Updates only the `extendedFields` field.
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<NonNullablePaths<UpdateExtendedFieldsResponse, `namespace`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1Menu.updateExtendedFields(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

/**
 * > **Note:** The Menu API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
 *
 * Deletes a menu.
 * @param menuId - Menu ID.
 * @public
 * @documentationMaturity preview
 * @requiredField menuId
 * @permissionId RESTAURANTS.MENU_DELETE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.RestaurantsMenusMenu.DeleteMenu
 */
export async function deleteMenu(menuId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ menuId: menuId });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.deleteMenu(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { menuId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['menuId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @param _id - Menu id to be duplicated.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId RESTAURANTS.MENU_CREATE
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.restaurants_duplicate_menu.RestaurantsDuplicateMenu.DuplicateMenu
 */
export async function duplicateMenu(
  _id: string,
  options?: DuplicateMenuOptions
): Promise<NonNullablePaths<DuplicateMenuResponse, `menuIds`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    businessLocationIds: options?.businessLocationIds,
    duplicateSubEntities: options?.duplicateSubEntities,
    menuName: options?.menuName,
  });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.duplicateMenu(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          businessLocationIds: '$[1].businessLocationIds',
          duplicateSubEntities: '$[1].duplicateSubEntities',
          menuName: '$[1].menuName',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DuplicateMenuOptions {
  /**
   * Business location ids to duplicate the menu to.
   * @format GUID
   * @maxSize 100
   */
  businessLocationIds?: string[] | null;
  /** Indicates whether sub-entities (such as items) should be duplicated as well. */
  duplicateSubEntities?: boolean | null;
  /**
   * The new name of the duplicated menu.
   * @maxLength 500
   */
  menuName?: string | null;
}

/**
 * Get menu details and path URL by menu ID.
 * @param _id - Menu ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId RESTAURANTS.MENU_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.restaurants_menus_site_url.RestaurantsMenusSiteUrl.GetMenuSiteUrl
 */
export async function getMenuSiteUrl(
  _id: string
): Promise<NonNullablePaths<GetMenuSiteUrlResponse, `menuSiteUrl.path`, 3>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts = ambassadorWixRestaurantsMenusV1Menu.getMenuSiteUrl(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Query menu details and path URL
 * @public
 * @documentationMaturity preview
 * @permissionId RESTAURANTS.MENU_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.menus_menu.v1.restaurants_menus_site_url.RestaurantsMenusSiteUrl.QueryMenusSiteUrl
 */
export async function queryMenusSiteUrl(
  options?: QueryMenusSiteUrlOptions
): Promise<
  NonNullablePaths<
    QueryMenusSiteUrlResponse,
    `menuSiteUrls` | `menuSiteUrls.${number}.path`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: options?.query,
  });

  const reqOpts =
    ambassadorWixRestaurantsMenusV1Menu.queryMenusSiteUrl(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0].query' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryMenusSiteUrlOptions {
  /** Query options */
  query?: CursorQuery;
}
